import { addTo, getOrCreate } from '../../../util/index.js';
import { isCreditNote } from '../../vouchers/matcher.js';
import { activateShadows } from './accrual.js';
import { allocateDynamic, applyDistributions, applyEarmarks, compareAllocation, noteJointTargets, } from './allocation.js';
import { claimableOpen, claims } from './claims.js';
import { correctsInterest, noteCorrectionCaps } from './corrections.js';
import { isoDateIn, NEVER } from './dates.js';
import { compareEvents, isAssociationSourceVoucher } from './events.js';
import { history, writeOffAccruals } from './history.js';
import { isJointCreditNote, mirror, pinOwn } from './joint.js';
import { expiryOf, ledgerOpen, trancheOpen } from './ledger.js';
import { applyWaivers, extendAll } from './limitation.js';
import { writeAmount, writeWriteOffAmounts } from './output.js';
import { startReplay } from './prepare.js';
import { advance, catchUp, markAppended, takeSnapshots, yieldIfDue, } from './timeline.js';
const appendEntry = async (run, entry) => {
    const caseState = entry.caseState;
    const at = Math.min(entry.day, caseState.until);
    await catchUp(caseState, entry.day);
    if (caseState.accruedTo !== null && caseState.accruedTo < at) {
        await advance(caseState, at);
    }
    takeSnapshots(caseState, entry.day);
    // Vouchers after `until` are allocated but are not yet events.
    if (entry.interruptsLimitation && entry.day <= caseState.until) {
        extendAll(caseState, at, 'ordinary');
    }
    if (entry.day <= caseState.until) {
        noteCorrectionCaps(caseState, entry, run.releaseCreditCaps);
    }
    markAppended(caseState, entry, entry.day);
    caseState.inEvent = true;
    const fixedJoint = new Map();
    const earmarkJoint = new Map();
    const replayJoint = new Map();
    applyDistributions(run, entry, fixedJoint);
    applyEarmarks(run, entry, earmarkJoint);
    // Joint mirrors pay only through their pinned distributions.
    if (!(entry.isMirror && caseState.associated)) {
        allocateDynamic(run, caseState, entry, replayJoint);
    }
    caseState.inEvent = false;
    caseState.eventClaims = undefined;
    if (isJointCreditNote(entry)) {
        for (const paid of [earmarkJoint, replayJoint]) {
            noteJointTargets(run.creditNotesPaid, entry, paid.get(entry) ?? []);
        }
    }
    for (const [source, targets] of earmarkJoint) {
        noteJointTargets(fixedJoint, source, targets);
    }
    // Manual distributions and earmarks reproduce themselves; mirrors follow
    // everything.
    for (const [source, targets] of replayJoint) {
        if (run.cases.length > 1) {
            pinOwn(run, source, targets);
        }
        noteJointTargets(fixedJoint, source, targets);
    }
    for (const [source, targets] of fixedJoint) {
        await mirror(run, source, at, targets);
    }
};
const caseResult = async (caseState) => {
    await catchUp(caseState, caseState.until);
    if (caseState.accruedTo !== null) {
        activateShadows(caseState, caseState.until);
    }
    applyWaivers(caseState);
    takeSnapshots(caseState, Number.POSITIVE_INFINITY);
    const claimsByLedger = claims(caseState);
    const isoDate = isoDateIn(caseState.policy.timezone);
    for (const entry of caseState.entries) {
        writeAmount(entry, claimsByLedger);
        if (entry.ledger === null) {
            continue;
        }
        const { ledger } = entry;
        writeWriteOffAmounts(entry);
        let nextExpiry = NEVER;
        let lastExpiry = Number.NEGATIVE_INFINITY;
        if (entry.kind === 'debt') {
            for (const tranche of ledger.tranches) {
                if (tranche.reason === null && trancheOpen(tranche) > 0) {
                    nextExpiry = Math.min(nextExpiry, expiryOf(ledger, tranche));
                }
                else if (tranche.expiredAt !== null) {
                    lastExpiry = Math.max(lastExpiry, expiryOf(ledger, tranche));
                }
            }
        }
        entry.voucher.nextExpiryDate =
            nextExpiry === NEVER ? null : isoDate(nextExpiry);
        entry.voucher.lastExpiryDate =
            lastExpiry === Number.NEGATIVE_INFINITY ? null : isoDate(lastExpiry);
        if (entry.kind === 'interest') {
            entry.voucher.amountUpdated = true;
            const claim = claimsByLedger?.get(ledger);
            if (claim !== undefined &&
                claimableOpen(ledger, claim) < ledgerOpen(ledger)) {
                entry.voucher.costCeilingApplied = true;
            }
            if (caseState.options.appendHist === true) {
                entry.voucher.interestHistory = history(entry);
            }
            if (caseState.options.writeOffAccruals === true) {
                entry.voucher.expiredAccruals = writeOffAccruals(entry, 'expired');
                entry.voucher.irrecoverableAccruals = writeOffAccruals(entry, 'irrecoverable');
            }
        }
    }
    // v1's final state: sorted relative to the last appended voucher, which
    // itself comes last.
    const last = caseState.lastAppended;
    const ordered = last === null
        ? caseState.entries
        : caseState.entries
            .filter((entry) => entry !== last)
            .toSorted((a, b) => compareAllocation(a, b, last))
            .concat(last);
    return {
        caseId: caseState.caseId,
        caseVouchers: ordered.map((entry) => entry.voucher),
        snapshots: caseState.snapshots,
    };
};
const walk = async (run) => {
    const entries = run.cases.flatMap((caseState) => caseState.entries);
    entries.sort((a, b) => compareEvents(a, b) || a.caseState.caseIndex - b.caseState.caseIndex);
    for (const entry of entries) {
        await yieldIfDue(entry.caseState);
        await appendEntry(run, entry);
    }
};
const observes = (input) => input.options.breakPointCallBack !== undefined ||
    input.options.snapshotTimes !== undefined;
const unobserved = (input) => ({
    ...input,
    options: {
        ...input.options,
        breakPointCallBack: undefined,
        snapshotTimes: undefined,
    },
});
const collectResults = async (run, passes) => {
    const results = [];
    for (const caseState of run.cases) {
        results.push(await caseResult(caseState));
    }
    return { results, joint: run.jointAllocations, passes };
};
// Whether derive mode has a joint credit note whose payments it corrects.
const hasJointCreditNote = (input) => input.associated &&
    input.caseVouchers.some((voucher) => voucher.deletedAt == null &&
        isCreditNote(voucher) &&
        isAssociationSourceVoucher(voucher));
// What the next replay corrects: what the joint credit notes corrected,
// lowered to what each found (E7), and what they still paid (D11).
const nextCreditCorrections = (run, current) => {
    const next = new Map([...current].map(([sourceId, amounts]) => [sourceId, new Map(amounts)]));
    for (const caseState of run.cases) {
        for (const { id, source, target, byCreditNote } of caseState.corrections) {
            const cap = caseState.correctionCaps.get(id);
            if (byCreditNote && cap !== undefined) {
                next.get(source.caseVoucherId).set(target.caseVoucherId, cap);
            }
        }
    }
    for (const [source, targets] of run.creditNotesPaid) {
        const amounts = getOrCreate(next, source.caseVoucherId, () => new Map());
        for (const [target, amount] of targets) {
            addTo(amounts, target.caseVoucherId, amount);
        }
    }
    return next;
};
// The largest change of any credit note's correction between two passes.
const largestMove = (before, after) => {
    let largest = 0;
    for (const sourceId of new Set([...before.keys(), ...after.keys()])) {
        const a = before.get(sourceId);
        const b = after.get(sourceId);
        for (const targetId of new Set([
            ...(a?.keys() ?? []),
            ...(b?.keys() ?? []),
        ])) {
            largest = Math.max(largest, Math.abs((b?.get(targetId) ?? 0) - (a?.get(targetId) ?? 0)));
        }
    }
    return largest;
};
// What a credit note corrects converges geometrically, by one ratio: correcting
// more principal leaves less interest to correct, which frees credit for more
// principal. From three passes in a row this takes the ratio of the correction
// that moved most, the same way and less each time, and jumps every
// correction of the credit note to where it converges (Aitken), within the
// credit note's amount. Null where there is nothing to jump.
const extrapolate = (run, [first, second, third]) => {
    let jumped = false;
    const next = new Map();
    for (const [sourceId, amounts] of third) {
        const steps = [
            ...new Set([
                ...(first.get(sourceId)?.keys() ?? []),
                ...(second.get(sourceId)?.keys() ?? []),
                ...amounts.keys(),
            ]),
        ].map((targetId) => {
            const x0 = first.get(sourceId)?.get(targetId) ?? 0;
            const x1 = second.get(sourceId)?.get(targetId) ?? 0;
            const x2 = amounts.get(targetId) ?? 0;
            return { targetId, x2, moved: x2 - x1, ratio: (x2 - x1) / (x1 - x0) };
        });
        const leading = steps
            .filter((step) => step.ratio > 0 && step.ratio < 1)
            .toSorted((a, b) => Math.abs(b.moved) - Math.abs(a.moved))[0];
        if (leading === undefined) {
            next.set(sourceId, amounts);
            continue;
        }
        const ratio = leading.ratio;
        const limits = steps.map((step) => {
            const limit = Math.max(0, Math.round(step.x2 + (step.moved * ratio) / (1 - ratio)));
            return {
                ...step,
                kept: Math.min(limit, step.x2),
                added: Math.max(0, limit - step.x2),
            };
        });
        // What grows shares what the credit note has left.
        const credit = run.cases
            .map((caseState) => caseState.byId.get(sourceId))
            .find((entry) => entry !== undefined);
        const room = credit.inputAmount - limits.reduce((sum, step) => sum + step.kept, 0);
        const added = limits.reduce((sum, step) => sum + step.added, 0);
        const share = added > room ? Math.max(0, room) / added : 1;
        next.set(sourceId, new Map(limits.map((step) => [
            step.targetId,
            step.kept + Math.floor(step.added * share),
        ])));
        jumped = true;
    }
    return jumped ? next : null;
};
// Ends the search for what credit notes correct where it still moves by
// more than an øre: what they then still pay is paid at its date.
const MAX_CORRECTION_PASSES = 10;
// Corrections of interest are lowered to what they found and the cases
// replayed until none finds less (E7); a lowered correction can only lower
// later ones. In derive mode what joint credit notes paid of joint debt is
// replayed as corrections (D11), each pass freeing what a correction of
// interest did not find for its credit note to pay, until no correction moves
// by more than an øre. Observers see only the final replay, which applies the
// corrections found as they are, as read mode does.
const replay = async (mode, inputs, untilISOString) => {
    let current = inputs;
    let creditCorrections = new Map();
    let passes = 0;
    if (inputs.some(correctsInterest) ||
        (mode === 'derive' && inputs.some(hasJointCreditNote))) {
        let iterates = [creditCorrections];
        for (; passes < MAX_CORRECTION_PASSES; passes++) {
            const run = startReplay(mode, current.map(unobserved), untilISOString, creditCorrections);
            run.releaseCreditCaps = true;
            await walk(run);
            if (run.creditNotesPaid.size === 0 &&
                run.cases.every((caseState) => caseState.correctionCaps.size === 0)) {
                if (!current.some(observes)) {
                    return collectResults(run, passes + 1);
                }
                break;
            }
            const distributionsCapped = run.cases.some((caseState) => caseState.corrections.some(({ id, byCreditNote }) => !byCreditNote && caseState.correctionCaps.has(id)));
            current = current.map((input, index) => {
                const caps = run.cases[index].correctionCaps;
                return {
                    ...input,
                    fixedDistributions: input.fixedDistributions.map((fd) => {
                        const cap = caps.get(fd.id);
                        return cap === undefined ? fd : { ...fd, amount: cap };
                    }),
                };
            });
            const next = nextCreditCorrections(run, creditCorrections);
            const settled = largestMove(creditCorrections, next) <= 1;
            creditCorrections = next;
            if (settled && !distributionsCapped) {
                passes++;
                break;
            }
            iterates = [...iterates, next].slice(-3);
            if (iterates.length === 3) {
                const jumped = extrapolate(run, iterates);
                if (jumped !== null) {
                    creditCorrections = jumped;
                    iterates = [jumped];
                }
            }
        }
    }
    const run = startReplay(mode, current, untilISOString, creditCorrections);
    await walk(run);
    return collectResults(run, passes + 1);
};
export const replayCase = async (input, untilISOString) => {
    const { results } = await replay('read', [input], untilISOString);
    return results[0];
};
export const replayAssociation = (inputs, untilISOString) => replay('derive', inputs, untilISOString);
//# sourceMappingURL=replay.js.map